import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ErrorAlert from 'components/ui/ErrorAlert';
import Link from 'components/ui/Link';
import { api } from 'helpers/axios';
import { errorMessage } from 'helpers/common';
import { toast } from 'helpers/toast';
import { useTranslation } from 'translation/useTranslation';
import ActionChangePreview from './ActionChangePreview';
import EmployeeSelector from './EmployeeSelector';
import { applySelectionChange, buildWhoList } from './actionChangePreviewHelpers';
import {
  ALL_MATCHING_SELECTION_MODE,
  EMPLOYEE_SELECTION_MODE,
  buildPeriodActivityPayload,
  canSubmitPeriodActivityAction,
} from './employeePeriodActivityActionHelpers';
import { Button } from "components/ui";
import useEmployeePool from './useEmployeePool';

export default function ActivateEmployeesAction({ filters, page, setPage, filterCount, setFilterCount }) {
  const navigate = useNavigate();
  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [selectionMode, setSelectionMode] = useState(EMPLOYEE_SELECTION_MODE);
  const [allMatchingReady, setAllMatchingReady] = useState(true);

  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [poolSearch, setPoolSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPoolPage(1);
    setSelected([]);
    setSelectedEmployees({});
    setSelectionMode(EMPLOYEE_SELECTION_MODE);
    setAllMatchingReady(true);
    setSearchInput('');
    setPoolSearch('');
    setPage(1);
  }, [filters, setPage]);

  const { employees, pagination, loading: loadingPool, loaded: poolLoaded, error: poolError } = useEmployeePool({
    filters,
    page: poolPage,
    perPage: poolPerPage,
    search: poolSearch,
    onTotal: (total) => {
      setFilterCount(total);
      setAllMatchingReady(true);
    },
  });

  // Each new pool request clears the shown error, pool or apply.
  useEffect(() => {
    setError(null);
  }, [filters, poolPage, poolPerPage, poolSearch]);

  useEffect(() => {
    if (poolError) setError(poolError);
  }, [poolError]);

  const handlePoolPerPageChange = (value) => {
    setPoolPerPage(value);
    setPoolPage(1);
  };

  const selectionIsValid = canSubmitPeriodActivityAction({
    selectionMode,
    selected,
    total: pagination.total,
  });
  const canContinue = selectionIsValid
    && (selectionMode !== ALL_MATCHING_SELECTION_MODE || allMatchingReady);

  const selectAllMatching = () => {
    const searchIsApplied = Boolean(searchInput.trim() || poolSearch);

    setSelectionMode(ALL_MATCHING_SELECTION_MODE);
    setAllMatchingReady(!searchIsApplied);
    setSearchInput('');
    setPoolSearch('');
    setPoolPage(1);
  };

  const handleApply = async () => {
    if (!canContinue) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = buildPeriodActivityPayload({ filters, selectionMode, selected });
      const { status } = await api.post('/employees/bulk-activate', payload);

      if (status === 200) {
        toast.success(translate('action.detail.activate_employees.success', 'Beschäftigte wurden aktiviert.'));
        navigate('/settings/actions');
      }
    } catch (err) {
      setError(errorMessage(err, 'activate employees'));
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  const poolPaginationProps = {
    search: searchInput,
    onSearchChange: setSearchInput,
    total: pagination.total,
    loadingPool,
    page: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    totalRows: pagination.total,
    setPage: setPoolPage,
    setPerPage: handlePoolPerPageChange,
  };

  return (
    <div className="mt-3">
      <hr className="mt-2 mb-4" />

      {error && <div className="mb-3">
        <ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} />
      </div>}

      {!poolLoaded ? <div className="text-center p-4" data-testid="loading">
        <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
      </div> : filterCount === 0 ? <>
        <div className="alert alert-warning">
          {editableTranslate('action.detail.no_employees_found', 'Keine Beschäftigten gefunden, die diesen Kriterien entsprechen.')}
        </div>
        <div className="settings-sticky-footer">
          <Button data-testid="back-button" as={Link} variant="secondary" to="/settings/actions">
            {editableTranslate('button.back', 'Zurück')}
          </Button>
        </div>
      </> : <>
        {page === 1 && <>
          {selectionMode === EMPLOYEE_SELECTION_MODE && <div className="mb-5">
            <div className="font-weight-bold">{editableTranslate('action.detail.employees', 'Beschäftigte')}</div>
            <hr className="my-2" />
            <div className="mb-3">
              {editableTranslate('action.detail.select_employees', 'Wählen Sie die Beschäftigten aus, auf die die Änderungen angewendet werden sollen.')}
            </div>
            <EmployeeSelector
              employees={employees || []}
              selected={selected}
              setSelected={setSelected}
              onSelectionChange={(items, checked) => setSelectedEmployees(previous => applySelectionChange(previous, items, checked))}
              {...poolPaginationProps}
            />
          </div>}

          <div className="mb-4">
            <div className="font-weight-bold">
              {editableTranslate('action.detail.activate_employees.scope', 'Beschäftigte aktivieren')}
            </div>
            <hr className="my-2" />
            <div className="form-check mb-2">
              <label className="form-check-label">
                <input
                  className="form-check-input"
                  type="radio"
                  name="activation-selection-mode"
                  checked={selectionMode === EMPLOYEE_SELECTION_MODE}
                  onChange={() => setSelectionMode(EMPLOYEE_SELECTION_MODE)} data-testid="activation-selection-mode-radio"
                />
                <span className="ml-1">
                  {editableTranslate('action.detail.activate_employees.selected', 'Ausgewählte Beschäftigte aktivieren')}
                </span>
              </label>
            </div>
            <div className="form-check">
              <label className="form-check-label">
                <input
                  className="form-check-input"
                  type="radio"
                  name="activation-selection-mode"
                  checked={selectionMode === ALL_MATCHING_SELECTION_MODE}
                  onChange={selectAllMatching} data-testid="activation-selection-mode-radio"
                />
                <span className="ml-1">
                  <span className="font-weight-bold">{pagination.total}</span>{' '}
                  {editableTranslate(
                    'action.detail.activate_employees.all_matching',
                    'Alle Beschäftigten aktivieren, die diesem Filter entsprechen.'
                  )}
                </span>
              </label>
            </div>
          </div>

          <div className="settings-sticky-footer mt-3">
            <Button data-testid="back-button" as={Link} variant="secondary" className="mr-2" to="/settings/actions">
              {editableTranslate('button.back', 'Zurück')}
            </Button>
            <Button variant="primary" onClick={() => setPage(2)} disabled={!canContinue} data-testid="next-button">
              {editableTranslate('button.next', 'Weiter')}
            </Button>
          </div>
        </>}

        {page === 2 && <>
          <ActionChangePreview
            whatItems={[{
              label: editableTranslate('action.detail.activate_employees.scope', 'Beschäftigte aktivieren'),
            }]}
            whoList={selectionMode === EMPLOYEE_SELECTION_MODE ? buildWhoList(selected, selectedEmployees) : []}
            count={selectionMode === ALL_MATCHING_SELECTION_MODE ? pagination.total : selected.length}
            showWhoList={selectionMode === EMPLOYEE_SELECTION_MODE}
          />

          <div className="settings-sticky-footer d-flex mt-3">
            <Button variant="secondary" onClick={() => setPage(1)} disabled={submitting} data-testid="back-button" className="mr-2">
              {editableTranslate('button.back', 'Zurück')}
            </Button>
            <Button variant="primary" onClick={handleApply} loading={submitting} disabled={!canContinue} data-testid="proceed-button">{editableTranslate('button.proceed', 'Fortfahren')}</Button>
          </div>
        </>}
      </>}
    </div>
  );
}
